import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { mockArticles } from '@/mocks/articleData';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = mockArticles.find((a) => a.id === id);
  const relatedArticles = mockArticles.filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.category },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="bg-secondary-100 text-secondary-600 px-2 py-0.5 rounded-md text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>
                {article.views}阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full aspect-video mb-8 rounded-lg overflow-hidden">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 青青草视频影视资讯`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose max-w-none text-foreground-800 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share */}
          <div className="mt-10 pt-6 border-t border-background-200 flex items-center gap-3">
            <span className="text-sm text-foreground-500">分享到：</span>
            {[
              { icon: 'ri-wechat-line', label: '微信', color: 'bg-green-500' },
              { icon: 'ri-weibo-line', label: '微博', color: 'bg-red-500' },
              { icon: 'ri-link', label: '复制链接', color: 'bg-foreground-500' },
            ].map((item) => (
              <button
                key={item.label}
                className={`w-9 h-9 flex items-center justify-center ${item.color} text-background-50 rounded-full cursor-pointer hover:opacity-80 transition-opacity`}
                title={item.label}
              >
                <i className={item.icon}></i>
              </button>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-14">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                <i className="ri-links-line text-primary-500 mr-2"></i>
                相关推荐
              </h2>
              <div className="space-y-4">
                {relatedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '青青草视频',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': typeof window !== 'undefined' ? window.location.href : '',
            },
          }),
        }}
      />
    </>
  );
}